<script setup lang="ts">
import { watch } from 'vue'

import { chartColor, useChartJs } from '@/shared/lib'

import { seriesColours } from './seriesColours'
import type { SeriesLineChartProps } from './types'

const props = withDefaults(defineProps<SeriesLineChartProps>(), {
  categoryField: 'date',
  formatValue: (value: number) => String(value),
  totalTitle: undefined,
  height: 400
})

const valueOf = (row: Record<string, unknown>, field: string) =>
  Number(row[field] ?? 0)

const totalOf = (row: Record<string, unknown>) =>
  props.series.reduce((sum, line) => sum + valueOf(row, line.field), 0)

const { element, render } = useChartJs({
  hasData: () => props.data.length > 0,
  draw: ({ Chart }, host) => {
    const colours = seriesColours(props.series.length)
    const totalColour = chartColor('--el-text-color-primary')

    return new Chart(host, {
      type: 'line',
      data: {
        labels: props.data.map((row) => String(row[props.categoryField])),
        datasets: [
          ...props.series.map((line, index) => ({
            label: line.title,
            data: props.data.map((row) => valueOf(row, line.field)),
            borderColor: colours[index],
            // `#rrggbb` plus an alpha byte, 15%: seven areas at `RealtimeChart`'s 30% blur
            // into one.
            backgroundColor: `${colours[index]}26`,
            borderWidth: 2,
            fill: 'origin' as const,
            pointRadius: 2,
            tension: 0.23
          })),
          ...(props.totalTitle
            ? [
                {
                  label: props.totalTitle,
                  data: props.data.map(totalOf),
                  hidden: true,
                  borderColor: totalColour,
                  backgroundColor: totalColour,
                  borderWidth: 3,
                  fill: false,
                  pointRadius: 3,
                  tension: 0.23
                }
              ]
            : [])
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            // A day's tooltip lists every line, and a line at zero that day is noise.
            filter: (item) => Number(item.raw) !== 0,
            callbacks: {
              label: (item) =>
                `${item.dataset.label}: ${props.formatValue(Number(item.raw))}`
            }
          }
        },
        scales: {
          // Tilted rather than thinned: a month of dates upright drops every other label.
          x: { ticks: { autoSkip: false, maxRotation: 60, minRotation: 60 } },
          y: {
            beginAtZero: true,
            ticks: { callback: (value) => props.formatValue(Number(value)) }
          }
        }
      }
    })
  }
})

watch(() => [props.data, props.series], render, { flush: 'post' })
</script>

<template>
  <div class="series-line-chart" :style="{ height: `${height}px` }">
    <canvas ref="element" />
  </div>
</template>

<style lang="scss" scoped>
.series-line-chart {
  position: relative;
}
</style>
